<script setup>
import { ref } from "vue";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
// import { Calendar } from '@fullcalendar/core';
import thLocale from "@fullcalendar/core/locales/th.js";

import { APP_CONFIG } from "@/config/app.config";

import { Modal } from "bootstrap";
import { onMounted } from "vue";
import calendarApi from "@/services/calendarApi";

import { useSwal } from "@/composables/useSwal";
import { computed } from "vue";
import dayjs from "dayjs";
const { success, error, confirm, loading, close } = useSwal();

import { defineProps } from "vue";

const props = defineProps({
  viewOnly: {
    type: Boolean,
    default: false,
  },
});

const today = new Date();
today.setHours(0, 0, 0, 0);

const minDate = new Date();
minDate.setHours(0, 0, 0, 0);

const daysUntil = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const target = new Date(date);
  target.setHours(0, 0, 0, 0);

  const diffTime = target - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

const upcomingMeetings = computed(() => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0);

  return events.value
    .filter((e) => {
      const startDate = new Date(e.start);
      startDate.setHours(0, 0, 0, 0);

      return (
        startDate >= today &&
        startDate >= startOfMonth &&
        startDate <= endNextMonth
      );
    })
    .sort((a, b) => new Date(a.start) - new Date(b.start));
});

/* ---------------- DATA ---------------- */
const events = ref([
  {
    id: "",
    title: "",
    start: "",
    end: "",
    extendedProps: {
      note: "",
    },
    color: "",
  },
]);

const form = ref({
  id: null,
  title: "",
  start: null,
  end: null,
  note: "",
  color: "",
});

const isEdit = ref(false);
let modalInstance = null;

const openModal = () => {
  modalInstance = new Modal(document.getElementById("eventModal"));
  modalInstance.show();
};

const resetForm = () => {
  form.value = {
    id: null,
    title: "",
    start: null,
    end: null,
    note: "",
    color: "",
  };
  isEdit.value = false;
};

/* ---------------- CRUD ---------------- */
// const saveEvent = () => {
//   if (isEdit.value) {
//     const idx = events.value.findIndex((e) => e.id === form.value.id);
//     events.value[idx] = {
//       ...events.value[idx],
//       title: form.value.title,
//       start: form.value.start.toISOString(),
//       end: form.value.end.toISOString(),
//       extendedProps: { note: form.value.note },
//     };
//   } else {
//     events.value.push({
//       id: Date.now().toString(),
//       title: form.value.title,
//       start: form.value.start.toISOString(),
//       end: form.value.end.toISOString(),
//       extendedProps: { note: form.value.note },
//     });
//   }

//   modalInstance.hide();
//   resetForm();
// };

const saveEvent = async () => {
  const payload = {
    title: form.value.title,
    start: dayjs(form.value.start).format("YYYY-MM-DD HH:mm:ss"),
    end: dayjs(form.value.end).format("YYYY-MM-DD HH:mm:ss"),
    note: form.value.note,
    color: form.value.color,
  };

  try {
    loading("กำลังบันทึก...");

    if (isEdit.value) {
      await calendarApi.updateEvent(form.value.id, payload);
    } else {
      await calendarApi.createEvent(payload);
    }

    // reload event
    await fetchEvents();

    close(); // ปิด loading
    modalInstance.hide();
    resetForm();

    success("บันทึกสำเร็จ");
  } catch (err) {
    close(); // ปิด loading เสมอ

    error(
      "เกิดข้อผิดพลาด",
      err.response?.data?.message || "ไม่สามารถบันทึกข้อมูลได้"
    );
  }
};

// const deleteEvent = () => {
//   events.value = events.value.filter((e) => e.id !== form.value.id);
//   modalInstance.hide();
//   resetForm();
// };
const deleteEvent = async () => {
  try {
    const result = await confirm(
      "ยืนยันการลบ",
      "คุณต้องการลบการประชุมนี้หรือไม่ ?"
    );

    if (!result.isConfirmed) return;

    loading("กำลังลบข้อมูล...");

    await calendarApi.deleteEvent(form.value.id);

    await fetchEvents();

    close();
    modalInstance.hide();
    resetForm();

    success("ลบเรียบร้อย");
  } catch (err) {
    close();

    error(
      "เกิดข้อผิดพลาด",
      err.response?.data?.message || "ไม่สามารถลบข้อมูลได้"
    );
  }
};

/* ---------------- CALENDAR ---------------- */
const calendarOptions = ref({
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
  initialView: "dayGridMonth",
  locale: thLocale,
  eventDisplay: "block",
  displayEventTime: false,
  events,
  selectable: !props.viewOnly,
  editable: !props.viewOnly,
  eventStartEditable: !props.viewOnly,
  eventDurationEditable: !props.viewOnly,
  dateClick(info) {
    if (props.viewOnly) return;

    resetForm();
    const date = new Date(info.dateStr);
    date.setHours(10, 0);
    const end = new Date(date);
    end.setHours(12, 0);

    form.value.start = date;
    form.value.end = end;
    openModal();
  },
  eventClick(info) {
    if (props.viewOnly) return;

    const now = new Date();
    if (info.event.start < now) {
      error("ไม่สามารถแก้ไขได้", "ไม่สามารถแก้ไขการประชุมย้อนหลังได้");
      return;
    }

    // แก้ไขได้เฉพาะอนาคต
    isEdit.value = true;
    const e = info.event;
    form.value.id = e.id;
    form.value.title = e.title;
    form.value.start = new Date(e.start);
    form.value.end = new Date(e.end);
    form.value.color = e.backgroundColor || e.borderColor || "#0d6efd";
    form.value.note = e.extendedProps.note;
    openModal();
  },
});

const fetchEvents = async () => {
  try {
    const res = await calendarApi.getEvents();
    events.value = res.data.map((e) => ({
      id: e.id,
      title: e.title,
      start: e.start,
      end: e.end,
      backgroundColor: e.color,
      borderColor: e.color,
      textColor: "#ffffff",
      extendedProps: {
        note: e.note,
      },
    }));
  } catch (err) {
    error(
      "โหลดข้อมูลไม่สำเร็จ",
      err.response?.data?.message || "ไม่สามารถโหลดข้อมูลได้"
    );
  }
};

onMounted(async () => {
  loading("กำลังโหลดข้อมูล...");
  await fetchEvents();
  close();
});
</script>

<template>
  <div class="container my-2">
    <h4 class="mb-3 text-center header-title">
      📅 {{ APP_CONFIG.appName }}
      <h5>
        {{ APP_CONFIG.appNameEN }}
      </h5>
    </h4>
    
    <div class="row mb-4">
      <div class="col-xl-8 mt-2">
        <div class="calendar-wrapper">
          <FullCalendar :options="calendarOptions" />
        </div>
      </div>
      <div class="col-xl-4 mt-2">
        <div class="upcoming-meetings-card">
          <h5 class="card-header text-center">การประชุมที่จะมาถึง</h5>
          <ol class="list-group list-group-numbered">
            <li
              v-for="item in upcomingMeetings"
              :key="item.id"
              class="list-group-item d-flex justify-content-between align-items-start meeting-item"
            >
              <span
                class="ms-1 mt-1 rounded color-indicator"
                :style="{
                  backgroundColor: item.backgroundColor,
                }"
              ></span>
              <div class="ms-2 me-auto">
                <div class="fw-bold meeting-title">
                  {{ item.title }}
                </div>
                <small class="text-muted">
                  📍 {{ item.extendedProps.note || "-" }}
                </small>
                <div class="text-muted date-text">
                  📅
                  {{
                    new Date(item.start).toLocaleString("th-TH", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  }}
                </div>
              </div>

              <span
                class="badge rounded-pill"
                :class="
                  daysUntil(item.start) <= 7 ? 'badge-urgent' : 'badge-normal'
                "
              >
                อีก {{ daysUntil(item.start) }} วัน
              </span>
            </li>

            <li
              v-if="upcomingMeetings.length === 0"
              class="list-group-item text-center text-muted empty-state"
            >
              ไม่มีการประชุมในช่วงนี้
            </li>
          </ol>
        </div>
      </div>
    </div>

    <div v-if="!viewOnly">
      <!-- Modal -->
      <div class="modal fade" id="eventModal">
        <div class="modal-dialog">
          <div class="modal-content custom-modal">
            <form @submit.prevent="saveEvent">
              <div class="modal-header">
                <h5 class="modal-title">
                  {{ isEdit ? "แก้ไขการประชุม" : "เพิ่มการประชุม" }}
                </h5>
                <button
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="modal"
                />
              </div>

              <div class="modal-body">
                <div class="mb-2">
                  <label class="form-label">หัวข้อ</label>
                  <input
                    v-model="form.title"
                    class="form-control custom-input"
                    :disabled="viewOnly"
                    required
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">เริ่มประชุม</label>
                  <VueDatePicker
                    selectionDisabled="true"
                    v-model="form.start"
                    locale="th"
                    time-picker-inline
                    :enable-time-picker="true"
                    :min-date="minDate"
                    format="dd/MM/yyyy HH:mm"
                    :disabled="viewOnly"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">สิ้นสุดประชุม</label>
                  <VueDatePicker
                    v-model="form.end"
                    locale="th"
                    time-picker-inline
                    :enable-time-picker="true"
                    :min-date="form.start || minDate"
                    format="dd/MM/yyyy HH:mm"
                    :disabled="viewOnly"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">สถานที่ / หมายเหตุ</label>
                  <textarea
                    v-model="form.note"
                    class="form-control custom-input"
                    rows="3"
                    :disabled="viewOnly"
                  />
                </div>

                <div class="mb-3" v-if="!viewOnly">
                  <label class="form-label">สีรายการประชุม</label>
                  <div class="d-flex gap-2">
                    <button
                      v-for="c in [
                        '#0d6efd',
                        '#198754',
                        '#dc3545',
                        '#ffc107',
                        '#6f42c1',
                      ]"
                      :key="c"
                      type="button"
                      class="btn p-3 border color-btn"
                      :style="{ backgroundColor: c }"
                      @click="form.color = c"
                    >
                      <span v-if="form.color === c" class="checkmark">✓</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button
                  v-if="isEdit && !viewOnly"
                  type="button"
                  class="btn btn-danger me-auto delete-btn"
                  @click="deleteEvent"
                >
                  ลบ
                </button>
                <button
                  v-if="!viewOnly"
                  class="btn btn-seccondary save-btn"
                  type="submit"
                >
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
/* ธีมสีส้ม-เทา */
:root {
  --primary-orange: #116045;      /* main color */
  --secondary-orange: #0e523a;    /* darker shade */
  --light-orange: #e6f2ee;        /* light background tint */

  --dark-gray: #374151;
  --medium-gray: #6b7280;
  --light-gray: #f3f4f6;
  --border-gray: #d1d5db;
}

body {
  background: linear-gradient(135deg, var(--light-gray) 0%, #e5e7eb 100%);
}

.container {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
}

.header-title {
  color: var(--dark-gray);
  font-weight: 700;
  font-size: 1.75rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-orange);
  margin-bottom: 2rem;
}

/* Calendar Wrapper */
.calendar-wrapper {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--border-gray);
}

/* FullCalendar Customization */
.fc {
  font-family: inherit;
}

.fc-toolbar-title {
  color: var(--dark-gray) !important;
  font-weight: 700 !important;
  font-size: 1.5rem !important;
}

.fc-button-primary {
  background-color: var(--primary-orange) !important;
  border-color: var(--secondary-orange) !important;
  transition: all 0.3s ease;
}

.fc-button-primary:hover {
  background-color: var(--secondary-orange) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 140, 66, 0.3);
}

.fc-button-primary:not(:disabled):active,
.fc-button-primary:not(:disabled).fc-button-active {
  background-color: var(--secondary-orange) !important;
  border-color: var(--secondary-orange) !important;
}

.fc-daygrid-day-number {
  color: var(--dark-gray);
}

.fc-col-header-cell {
  background-color: var(--light-orange);
  color: var(--dark-gray);
  font-weight: 600;
}

.fc-event {
  cursor: pointer;
  border: none;
  border-radius: 6px;
  padding: 4px 6px;
  transition: all 0.2s ease;
}

.fc-event:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.fc-daygrid-day.fc-day-today {
  background-color: var(--light-orange) !important;
}

/* Upcoming Meetings Card */
.upcoming-meetings-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid var(--border-gray);
}

.card-header {
  background: linear-gradient(
    135deg,
    var(--primary-orange) 0%,
    var(--secondary-orange) 100%
  );
  color: white;
  padding: 1rem 1.25rem;
  font-weight: 600;
  margin: 0;
  font-size: 1.1rem;
}

.meeting-item {
  border: none;
  border-bottom: 1px solid var(--light-gray);
  padding: 1rem;
  transition: all 0.3s ease;
}

.meeting-item:hover {
  background-color: var(--light-orange);
  transform: translateX(5px);
}

.meeting-item:last-child {
  border-bottom: none;
}

.color-indicator {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.meeting-title {
  color: var(--dark-gray);
  font-size: 1rem;
}

.date-text {
  font-size: 0.875rem;
}

.badge-urgent {
  background: linear-gradient(135deg, var(--secondary-orange) 0%, #ff4500 100%);
  color: white;
  font-weight: 600;
}

.badge-normal {
  background: linear-gradient(
    135deg,
    var(--medium-gray) 0%,
    var(--dark-gray) 100%
  );
  color: white;
  font-weight: 600;
}

.empty-state {
  padding: 2rem;
  color: var(--medium-gray);
  font-style: italic;
}

/* Modal Customization */
.custom-modal .modal-header {
  background: linear-gradient(
    135deg,
    var(--primary-orange) 0%,
    var(--secondary-orange) 100%
  );
  color: white;
  border-bottom: none;
}

.custom-modal .modal-title {
  font-weight: 600;
}

.custom-modal .btn-close {
  filter: brightness(0) invert(1);
}

.custom-modal .modal-body {
  padding: 1.5rem;
  background-color: #fafafa;
}

.form-label {
  color: var(--dark-gray);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.custom-input {
  border: 2px solid var(--border-gray);
  border-radius: 8px;
  padding: 0.75rem;
  transition: all 0.3s ease;
}

.custom-input:focus {
  border-color: var(--primary-orange);
  box-shadow: 0 0 0 0.2rem rgba(255, 140, 66, 0.25);
  outline: none;
}

.color-btn {
  border-radius: 8px;
  width: 50px;
  height: 50px;
  transition: all 0.3s ease;
  border: 2px solid transparent !important;
}

.color-btn:hover {
  transform: scale(1.1);
  border: 2px solid var(--dark-gray) !important;
}

.checkmark {
  color: white;
  /* font-size: 1.5rem; */
  font-weight: bold;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.delete-btn {
  background-color: #ef4444;
  border: none;
  font-weight: 600;
  transition: all 0.3s ease;
}

.delete-btn:hover {
  background-color: #dc2626;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.save-btn {
  background: linear-gradient(
    135deg,
    var(--primary-orange) 0%,
    var(--secondary-orange) 100%
  );
  border: none;
  font-weight: 600;
  padding: 0.5rem 2rem;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 140, 66, 0.4);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-title {
    font-size: 1.5rem;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 1rem;
  }

  .calendar-wrapper {
    padding: 1rem;
  }
}
</style>
